import React, { useEffect, useState } from 'react';

import moment from 'moment';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { loadEmployeeGroups } from '../../employee-groups/actions/load-employee-groups';
import { userSelector } from '../../reducers/current-user-selectors';
import { orgSelector } from '../../reducers/org-selectors';
import {
  surveysForOrgIdSelector,
  surveysForOrgIdStateSelector,
} from '../../reducers/survey-selectors';
import { loadSurveys } from '../actions/survey/load-surveys';

import { FeatureFlagged, FeatureGatedVariant } from '../../feature-gates/feature-flagged';
import { FeatureGates } from '../../feature-gates/feature-gates';
import { MessagePreviewItem } from '../../messages/components/message-preview-item';
import { TextMessage } from '../../messages/legacy-components/text-message';
import { UiBreadcrumbHeader } from '../../ui/ui-breadcrumb-header';
import { UiButton } from '../../ui/ui-button';
import { surveysRoute } from '../../web-routes';

import { CreateSurveyDialog } from './create-survey-dialog';
import { EmployeeOnboardingExitSurveyTable } from './employee-onboarding-exit-survey-table';
import { EmployeeSurveyDetail } from './employee-survey-detail';
import { EmployeeSurveyTable } from './employee-survey-table';

import { formatPhoneNumber } from '../../util/formatters';
import { HeaderButtonAddIcon } from '../../util/material-design-icons';
import { MESSAGE_TYPE } from '../../util/message-types';
import { EMPLOYEE_PERMISSION } from '../../util/permissions';
import { SURVEY_TYPES } from '../../util/survey-types';
import { VCardPopoverIcon } from '../../widgets/contact-card/v-card-popover-icon';
import { LoadingMessage } from '../../widgets/loading-message';

const mapStateToProps = (state: any, { orgId }: InputProps) => ({
  surveys: surveysForOrgIdSelector(state, orgId),
  surveysState: surveysForOrgIdStateSelector(state, orgId),
  user: userSelector(state),
  org: orgSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    loadSurveys: () => dispatch(loadSurveys(ownProps.orgId)),
    loadOrgEmployeeGroups: () => dispatch(loadEmployeeGroups(ownProps.orgId)),
  },
});

type InputProps = {
  orgId: number;
  surveyId: number | null;
};
type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

const runningSurveys = (surveys: Array<Survey>) =>
  surveys.filter((survey) => moment.utc().isBetween(survey.sentAt, survey.endAt));

export const EmployeeSurveys = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const { surveys, actions, orgId, surveyId, user, surveysState, org } = props;

  const [emptyStateVisible, setEmptyStateVisible] = useState<boolean>(true);
  const [isCreatingNewSurvey, setIsCreatingNewSurvey] = useState<boolean>(false);
  const [isCreatingNewOnboardingSurvey, setIsCreatingNewOnboardingSurvey] =
    useState<boolean>(false);
  const [isCreatingNewExitSurvey, setIsCreatingNewExitSurvey] = useState<boolean>(false);
  const [userWantsEmptyState, setUserWantsEmptyState] = useState<boolean>(false);
  const [showContactCard, setShowContactCard] = useState<boolean>(false);

  useEffect(() => {
    actions.loadSurveys();
    actions.loadOrgEmployeeGroups();
  });

  if (!surveys || !surveysState) {
    return null;
  }

  if (surveysState.loading) {
    return <LoadingMessage />;
  }

  const handleCreateSurvey = () => setIsCreatingNewSurvey(true);
  const handleCreateOnboardingSurvey = () => setIsCreatingNewOnboardingSurvey(true);
  const handleCreateExitSurvey = () => setIsCreatingNewExitSurvey(true);

  const handleCloseCreateSurvey = () => setIsCreatingNewSurvey(false);
  const handleCloseCreateOnboardingSurvey = () => setIsCreatingNewOnboardingSurvey(false);
  const handleCloseCreateExitSurvey = () => setIsCreatingNewExitSurvey(false);

  const handleBestWayClick = () => setUserWantsEmptyState(true);
  const turnOffBestWay = () => setUserWantsEmptyState(false);

  const standardSurveys = surveys.filter((survey) => survey.surveyType === SURVEY_TYPES.STANDARD);
  const nonStandardSurveys = surveys.filter(
    (survey) => survey.surveyType !== SURVEY_TYPES.STANDARD,
  );
  const onboardingSurveys = nonStandardSurveys.filter(
    (survey) => survey.surveyType === SURVEY_TYPES.ONBOARDING,
  );
  const exitSurveys = nonStandardSurveys.filter(
    (survey) => survey.surveyType === SURVEY_TYPES.EXIT,
  );

  const currentlyRunningSurveys = {
    standard: runningSurveys(standardSurveys),
    onboarding: runningSurveys(onboardingSurveys),
    exit: runningSurveys(exitSurveys),
  };

  const previousSurveys = standardSurveys
    .filter((survey) => !moment.utc().isBetween(survey.sentAt, survey.endAt))
    .sort((a, b) => moment(b.sentAt).valueOf() - moment(a.sentAt).valueOf());

  const hasAllEmployeesPermission = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;

  const handleEmptyStateToggle = () => {
    setEmptyStateVisible(false);
    setIsCreatingNewSurvey(true);
  };

  const openShowContactCard = () => setShowContactCard(true);
  const closeShowContactCard = () => setShowContactCard(false);

  const renderEmptyState = () => (
    <div>
      <UiBreadcrumbHeader
        className="u-position-top-sticky u-background-offWhite"
        pageTitle="Surveys"
        primaryActions={
          hasAllEmployeesPermission
            ? [
                userWantsEmptyState
                  ? { name: 'Send A Survey', handler: turnOffBestWay }
                  : { name: 'Create Your First Survey', handler: handleEmptyStateToggle },
              ]
            : []
        }
      />
      <div className="u-maxWidth-750px u-padding u-textAlign-center u-margin-0Auto u-display-flex u-justifyContent-center u-flexDirection-column u-alignItems-center">
        <div className="typo-title u-margin-bottom-2x u-textAlign-left">
          <div className="u-textAlign-center u-margin-y-xl">
            With surveys, you can quickly gather important information from your employees to
            continuously improve as a company. You can make survey responses anonymous to encourage
            employees to provide candid feedback.
          </div>
          <div className="u-maxWidth-550px u-margin-top-lg">
            <div className="typo-bold">Workplace safety</div>
            <FeatureFlagged
              flagName={FeatureGates.INTEGRATED_MESSAGING}
              variant={FeatureGatedVariant.HIDE_ON_FLAG}
            >
              <TextMessage
                isAutomated
                senderName={`${user.firstName} ${user.lastName}`}
                sendDateTime={moment().format()}
                messageBody={`Hi ${user.firstName} - What is one way we can improve your safety at work?`}
              />
            </FeatureFlagged>
            <FeatureFlagged
              flagName={FeatureGates.INTEGRATED_MESSAGING}
              variant={FeatureGatedVariant.SHOW_ON_FLAG}
            >
              <MessagePreviewItem
                messageType={MESSAGE_TYPE.TEXT}
                senderName={`${user.firstName} ${user.lastName}`}
                sendDateTime={moment().format()}
                messageBody={`Hi ${user.firstName} - What is one way we can improve your safety at work?`}
              />
            </FeatureFlagged>
          </div>
          <div className="u-textAlign-right">
            <div className="u-margin-top-lg u-maxWidth-550px u-display-inlineBlock u-textAlign-left">
              <div className="typo-bold">Gather feedback on training/onboarding</div>
              <FeatureFlagged
                flagName={FeatureGates.INTEGRATED_MESSAGING}
                variant={FeatureGatedVariant.HIDE_ON_FLAG}
              >
                <TextMessage
                  isAutomated
                  senderName={`${user.firstName} ${user.lastName}`}
                  sendDateTime={moment().format()}
                  messageBody={`Hi ${user.firstName} - We are excited to have you here! Now that you have gone through training, can you please give us some feedback on how we can improve our onboarding process? We’d appreciate it!`}
                />
              </FeatureFlagged>
              <FeatureFlagged
                flagName={FeatureGates.INTEGRATED_MESSAGING}
                variant={FeatureGatedVariant.SHOW_ON_FLAG}
              >
                <MessagePreviewItem
                  messageType={MESSAGE_TYPE.TEXT}
                  senderName={`${user.firstName} ${user.lastName}`}
                  sendDateTime={moment().format()}
                  messageBody={`Hi ${user.firstName} - We are excited to have you here! Now that you have gone through training, can you please give us some feedback on how we can improve our onboarding process? We’d appreciate it!`}
                />
              </FeatureFlagged>
            </div>
          </div>
          <div className="u-maxWidth-550px u-margin-top-lg">
            <div className="typo-bold">Answering important questions across the team</div>
            <FeatureFlagged
              flagName={FeatureGates.INTEGRATED_MESSAGING}
              variant={FeatureGatedVariant.HIDE_ON_FLAG}
            >
              <TextMessage
                isAutomated
                senderName={`${user.firstName} ${user.lastName}`}
                sendDateTime={moment().format()}
                messageBody={`Hi ${user.firstName} - We are ordering new uniforms. Can you please tell us what shirt size you would like?`}
              />
            </FeatureFlagged>
            <FeatureFlagged
              flagName={FeatureGates.INTEGRATED_MESSAGING}
              variant={FeatureGatedVariant.SHOW_ON_FLAG}
            >
              <MessagePreviewItem
                messageType={MESSAGE_TYPE.TEXT}
                senderName={`${user.firstName} ${user.lastName}`}
                sendDateTime={moment().format()}
                messageBody={`Hi ${user.firstName} - We are ordering new uniforms. Can you please tell us what shirt size you would like?`}
              />
            </FeatureFlagged>
          </div>
          <div className="u-textAlign-right">
            <div className="u-margin-top-lg u-maxWidth-550px u-display-inlineBlock u-textAlign-left">
              <div className="typo-bold">Gather exit feedback</div>
              <FeatureFlagged
                flagName={FeatureGates.INTEGRATED_MESSAGING}
                variant={FeatureGatedVariant.HIDE_ON_FLAG}
              >
                <TextMessage
                  isAutomated
                  senderName={`${user.firstName} ${user.lastName}`}
                  sendDateTime={moment().format()}
                  messageBody={`Hi ${user.firstName} - Could you please let us know the primary reason you are leaving? We’d appreciate it and it would really help us improve`}
                />
              </FeatureFlagged>
              <FeatureFlagged
                flagName={FeatureGates.INTEGRATED_MESSAGING}
                variant={FeatureGatedVariant.SHOW_ON_FLAG}
              >
                <MessagePreviewItem
                  messageType={MESSAGE_TYPE.TEXT}
                  senderName={`${user.firstName} ${user.lastName}`}
                  sendDateTime={moment().format()}
                  messageBody={`Hi ${user.firstName} - Could you please let us know the primary reason you are leaving? We’d appreciate it and it would really help us improve`}
                />
              </FeatureFlagged>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (
    (surveysState && !surveysState.loading && surveys.length === 0 && emptyStateVisible) ||
    userWantsEmptyState
  ) {
    return renderEmptyState();
  }

  return (
    <div>
      <FeatureFlagged
        flagName={FeatureGates.INTEGRATED_MESSAGING}
        variant={FeatureGatedVariant.HIDE_ON_FLAG}
      >
        <UiBreadcrumbHeader
          className="u-position-top-sticky u-background-offWhite"
          pageTitle="Surveys"
          subsectionTitle={surveyId ? 'Survey Results' : undefined}
          backToPage={surveyId ? surveysRoute(orgId) : undefined}
          customPrimaryAction={
            hasAllEmployeesPermission && !surveyId ? (
              <UiButton
                onClick={handleCreateSurvey}
                dataCy="create-survey"
                startIcon={<HeaderButtonAddIcon />}
                size="sm"
              >
                Create Survey
              </UiButton>
            ) : null
          }
        />
      </FeatureFlagged>
      <FeatureFlagged
        flagName={FeatureGates.INTEGRATED_MESSAGING}
        variant={FeatureGatedVariant.SHOW_ON_FLAG}
      >
        <UiBreadcrumbHeader
          className="u-position-top-sticky u-background-offWhite"
          pageTitle="Surveys"
          titleAction={
            surveyId
              ? undefined
              : {
                  name: '',
                  handler: openShowContactCard,
                  Icon: (
                    <VCardPopoverIcon
                      org={org}
                      variant="survey"
                      onClose={closeShowContactCard}
                      isOpen={showContactCard}
                    />
                  ),
                }
          }
          subsectionTitle={surveyId ? 'Survey Results' : undefined}
          backToPage={surveyId ? surveysRoute(orgId) : undefined}
          customPrimaryAction={
            hasAllEmployeesPermission && !surveyId ? (
              <UiButton
                onClick={handleCreateSurvey}
                dataCy="create-survey"
                startIcon={<HeaderButtonAddIcon />}
                size="sm"
              >
                Create Survey
              </UiButton>
            ) : null
          }
        />
      </FeatureFlagged>
      <div className="u-margin-lg">
        {isCreatingNewSurvey ? (
          <CreateSurveyDialog
            orgId={orgId}
            onClose={handleCloseCreateSurvey}
            surveyType="standard"
          />
        ) : null}
        {isCreatingNewOnboardingSurvey ? (
          <CreateSurveyDialog
            orgId={orgId}
            onClose={handleCloseCreateOnboardingSurvey}
            surveyType="onboarding"
          />
        ) : null}
        {isCreatingNewExitSurvey ? (
          <CreateSurveyDialog
            orgId={orgId}
            onClose={handleCloseCreateExitSurvey}
            surveyType="exit"
          />
        ) : null}
        {org?.surveyPhoneNumber && (
          <>
            <div className="typo-subtitle">Survey Phone Number</div>
            <a className="typo-subtitle typo-link-secondary" href={`tel:${org.surveyPhoneNumber}`}>
              {formatPhoneNumber(org.surveyPhoneNumber)}
            </a>
          </>
        )}
        {surveyId ? (
          <div className="card u-padding u-margin-top">
            {surveyId ? <EmployeeSurveyDetail surveyId={surveyId} orgId={orgId} /> : null}
          </div>
        ) : (
          <>
            <div className="card u-padding u-margin-top u-overflowX-scroll">
              <div className="typo-subtitle">
                Surveys are questions that you send out to a group of people where you expect a
                response. Surveys will not be sent to employees whose start dates are in the future.
              </div>
              {hasAllEmployeesPermission ? (
                <div
                  role="presentation"
                  className="typo-subtitle typo-link"
                  onClick={handleBestWayClick}
                >
                  What&apos;s the best way to use surveys?
                </div>
              ) : null}
              {currentlyRunningSurveys.standard.length > 0 ? (
                <EmployeeSurveyTable
                  surveys={currentlyRunningSurveys.standard}
                  orgId={orgId}
                  isCurrentlyRunning
                />
              ) : null}
              {previousSurveys.length > 0 ? (
                <>
                  <div className="typo-h6 u-color-primary u-margin-top-xl">Completed</div>
                  <EmployeeSurveyTable surveys={previousSurveys} orgId={orgId} />
                </>
              ) : null}
            </div>
            <div className="card u-padding u-margin-top u-overflowX-scroll">
              <div className="u-spread">
                <div className="typo-h6 u-color-primary">Onboarding Survey</div>
              </div>
              <div className="typo-subtitle">
                An onboarding survey is a question that you send to an employee about their
                onboarding experience.
              </div>
              {onboardingSurveys.length === 0 ? (
                hasAllEmployeesPermission ? (
                  <UiButton
                    className="u-margin-top u-margin-bottom"
                    onClick={handleCreateOnboardingSurvey}
                  >
                    Create Onboarding Survey
                  </UiButton>
                ) : null
              ) : (
                <EmployeeOnboardingExitSurveyTable
                  surveys={onboardingSurveys}
                  orgId={orgId}
                  hasAllEmployeesPermission={hasAllEmployeesPermission}
                />
              )}
            </div>
            <div className="card u-padding u-margin-top u-overflowX-scroll">
              <div className="typo-h6 u-color-primary">Exit Survey</div>
              <div className="typo-subtitle">
                An exit survey is a question that you send to an employee to collect feedback after
                they have moved on from your company.
              </div>
              {exitSurveys.length === 0 ? (
                hasAllEmployeesPermission ? (
                  <UiButton
                    className="u-margin-top u-margin-bottom"
                    onClick={handleCreateExitSurvey}
                  >
                    Create Exit Survey
                  </UiButton>
                ) : null
              ) : (
                <EmployeeOnboardingExitSurveyTable
                  surveys={exitSurveys}
                  orgId={orgId}
                  hasAllEmployeesPermission={hasAllEmployeesPermission}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
});
